import { API } from "../config/axiosConfig";
import endpoints from "../endpoints/endpoints";

// Helper to normalize server error messages into user-friendly text
const friendlyMessage = (rawMessage, fallback) => {
  const msg = String(rawMessage || "");
  // Duplicate/unique constraint (e.g., seller_id already exists)
  if (/duplicate key value|unique constraint|already exists|seller_id/i.test(msg)) {
    return "You already have a payment method saved. Please update the existing method instead of adding a new one.";
  }
  // Common validation hints
  if (/iban|ibn/i.test(msg)) {
    return "Please enter a valid IBAN/IBN number.";
  }
  if (/account number/i.test(msg)) {
    return "Please check the account number and try again.";
  }
  return fallback || msg || "Something went wrong";
};

export const paymentModule = {
  requestPayout: async (payload) => {
    try {
      const response = await API.post(endpoints.requestPayout, payload);
      return {
        success: true,
        data: response.data,
        message:
          response.data.message || "Payout request submitted successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to submit payout request",
        error: error.response?.data || error,
      };
    }
  },

  getPayoutsList: async (params = {}) => {
    try {
      const { index = 0, offset = 10, ...otherParams } = params;
      const queryParams = new URLSearchParams({
        index: index.toString(),
        offset: offset.toString(),
        ...otherParams,
      });

      const response = await API.get(
        `${endpoints.getPayoutsList}?${queryParams}`
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message || "Payouts fetched successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch payouts",
        error: error.response?.data || error,
      };
    }
  },

  // Review Payout (Approve/Reject)
  reviewPayout: async (payload) => {
    try {
      const response = await API.post(endpoints.reviewPayout, payload);
      return {
        success: true,
        data: response.data,
        message:
          response.data.message || "Payout review completed successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to review payout",
        error: error.response?.data || error,
      };
    }
  },

  // Bank Details API
  addBankDetails: async (payload) => {
    try {
      const response = await API.post(endpoints.addBankDetails, payload);
      return {
        success: true,
        data: response.data,
        message: response.data.message || "Bank details added successfully",
      };
    } catch (error) {
      const raw = error.response?.data?.message || error.message;
      return {
        success: false,
        message: friendlyMessage(raw, "Failed to add bank details"),
        error: error.response?.data || error,
      };
    }
  },

  updateBankDetails: async (payload) => {
    try {
      const response = await API.put(endpoints.updateBankDetails, payload);
      return {
        success: true,
        data: response.data,
        message: response.data.message || "Bank details updated successfully",
      };
    } catch (error) {
      const raw = error.response?.data?.message || error.message;
      return {
        success: false,
        message: friendlyMessage(raw, "Failed to update bank details"),
        error: error.response?.data || error,
      };
    }
  },

  deleteBankDetails: async (bankAccountId) => {
    try {
      const response = await API.delete(
        `${endpoints.deleteBankDetails}?bank_account_id=${bankAccountId}`
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message || "Bank details deleted successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to delete bank details",
        error: error.response?.data || error,
      };
    }
  },

  listBankDetails: async () => {
    try {
      console.log("Fetching bank details from:", endpoints.listBankDetails);
      const response = await API.get(endpoints.listBankDetails);
      console.log("Raw API response for bank details:", response);
      console.log("Response data:", response.data);
      
      // Based on the provided API response structure
      if (response.data && response.data.success) {
        console.log("Returning structured response:", response.data.data);
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || "Bank details fetched successfully",
        };
      } else {
        // Fallback for different response structure
        console.log("Returning raw response:", response.data);
        return {
          success: true,
          data: response.data,
          message: "Bank details fetched successfully",
        };
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      console.error("Error response:", error.response);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch bank details",
        error: error.response?.data || error,
      };
    }
  },
};
